import {
  Property,
  Resident,
  BookingRequest,
  AttendanceRecord,
  StaffMember,
  StaffTask,
  BroadcastNotification,
  MealPlanDay,
  ChatMessage,
  MaintenanceTicket,
  Amenity,
  UserAccount,
} from './types';

// Demo mode mock data - loaded when demo users log in
export const DEMO_PROPERTIES: Property[] = [
  {
    id: 'demo-prop-1',
    name: 'Sunrise PG - Demo',
    tagline: 'Premium co-living in the heart of the city',
    gender: 'Unisex / Co-ed',
    city: 'Bengaluru',
    locality: 'Koramangala',
    address: '123 Demo Street, Koramangala, Bengaluru',
    lat: 12.93,
    lng: 77.62,
    coverImage: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=1000&q=80',
    galleryImages: ['https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=1000&q=80'],
    startingPrice: 8000,
    rating: 4.5,
    reviewCount: 156,
    rooms: [],
    amenities: ['wifi', 'ac', 'food', 'cctv'],
    rules: ['No smoking', 'No alcohol'],
    noticePeriodDays: 30,
    gateClosingTime: '11:00 PM',
    foodIncluded: true,
    verified: true,
    featured: true,
    contactPhone: '+91 9876543210',
    contactEmail: 'owner@sunrisepg.demo',
    ownerName: 'Demo Owner',
  },
  {
    id: 'demo-prop-2',
    name: 'Blue Haven Luxury PG - Demo',
    tagline: 'Affordable luxury with modern amenities',
    gender: 'Girls',
    city: 'Bengaluru',
    locality: 'HSR Layout',
    address: '456 Demo Avenue, HSR Layout, Bengaluru',
    lat: 12.91,
    lng: 77.64,
    coverImage: 'https://images.unsplash.com/photo-1560448214-e0f607d53b02?auto=format&fit=crop&w=1000&q=80',
    galleryImages: ['https://images.unsplash.com/photo-1560448214-e0f607d53b02?auto=format&fit=crop&w=1000&q=80'],
    startingPrice: 12000,
    rating: 4.8,
    reviewCount: 234,
    rooms: [],
    amenities: ['wifi', 'ac', 'gym', 'ro_water'],
    rules: ['No smoking', 'No visitors after 10 PM'],
    noticePeriodDays: 60,
    gateClosingTime: '10:00 PM',
    foodIncluded: true,
    verified: true,
    featured: true,
    contactPhone: '+91 9876543211',
    contactEmail: 'owner@bluehaven.demo',
    ownerName: 'Demo Owner 2',
  },
];

export const DEMO_RESIDENTS: Resident[] = [
  {
    id: 'demo-resident-1',
    name: 'Demo Resident 1',
    email: 'demo-resident@pgwalo.com',
    phone: '+91 9876543210',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80',
    propertyId: 'demo-prop-1',
    propertyName: 'Sunrise PG - Demo',
    roomNumber: '101',
    roomType: 'Double Sharing',
    bedNumber: 'A',
    monthlyRent: 8000,
    depositAmount: 8000,
    moveInDate: '2024-01-15',
    rentStatus: 'Paid',
    rentDueDate: '2024-02-15',
    emergencyContact: '+91 9876543211',
    role: 'resident',
    gender: 'Male',
    organizationId: 'org-demo-pgwalo',
    status: 'Active',
  },
  {
    id: 'demo-resident-2',
    name: 'Demo Resident 2',
    email: 'demo-resident2@pgwalo.com',
    phone: '+91 9876543212',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80',
    propertyId: 'demo-prop-2',
    propertyName: 'Blue Haven Luxury PG - Demo',
    roomNumber: '201',
    roomType: 'Private',
    bedNumber: '1',
    monthlyRent: 12000,
    depositAmount: 12000,
    moveInDate: '2024-02-01',
    rentStatus: 'Pending',
    rentDueDate: '2024-03-01',
    emergencyContact: '+91 9876543213',
    role: 'resident',
    gender: 'Female',
    organizationId: 'org-demo-pgwalo',
    status: 'Active',
  },
];

export const DEMO_BOOKING_REQUESTS: BookingRequest[] = [
  {
    id: 'demo-booking-1',
    applicantName: 'Demo Applicant',
    email: 'demo.applicant@pgwalo.com',
    phone: '+91 9876543214',
    propertyId: 'demo-prop-1',
    propertyName: 'Sunrise PG - Demo',
    roomType: 'Double Sharing',
    preferredMoveInDate: '2024-03-01',
    occupancyType: 'Working Professional',
    status: 'Pending',
    requestDate: '2024-02-15',
    type: 'booking',
  },
];

export const INITIAL_AMENITIES: Amenity[] = [
  { id: 'wifi', name: 'High-Speed 300 Mbps Wi-Fi', icon: 'Wifi', category: 'essential' },
  { id: 'ac', name: 'Air Conditioning', icon: 'Wind', category: 'comfort' },
  { id: 'food', name: '3-Time Homely Food', icon: 'Utensils', category: 'food' },
  { id: 'laundry', name: 'Washing Machine & Iron', icon: 'Shirt', category: 'essential' },
  { id: 'attached_bath', name: 'Attached Western Washroom', icon: 'Bath', category: 'comfort' },
  { id: 'power_backup', name: '24x7 Generator Backup', icon: 'Zap', category: 'essential' },
  { id: 'cctv', name: 'CCTV & Biometric Entry', icon: 'ShieldCheck', category: 'security' },
  { id: 'gym', name: 'Fitness Gym & Yoga Zone', icon: 'Dumbbell', category: 'comfort' },
  { id: 'ro_water', name: 'Mineral RO Water Dispenser', icon: 'Droplets', category: 'essential' },
  { id: 'housekeeping', name: 'Daily Room Housekeeping', icon: 'Sparkles', category: 'essential' },
  { id: 'geyser', name: 'Hot Water Geyser', icon: 'Flame', category: 'comfort' },
  { id: 'parking', name: 'Covered Bike & Car Parking', icon: 'Car', category: 'comfort' },
];

export const INITIAL_PROPERTIES: Property[] = [
  {
    id: 'prop-1',
    name: 'Blue Haven Luxury Living PG',
    tagline: 'Modern, high-tech co-living space with organic home-cooked meals',
    gender: 'Boys',
    city: 'Bengaluru',
    locality: 'HSR Layout, Sector 3',
    address: 'Plot 412, 14th Main Rd, Sector 3, HSR Layout, Bengaluru, Karnataka 560102',
    lat: 12.9116,
    lng: 77.6446,
    coverImage: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=1000&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=1000&q=80',
    ],
    startingPrice: 8500,
    rating: 4.8,
    reviewCount: 64,
    rooms: [
      {
        id: 'r1-single',
        type: 'Single',
        rentPerMonth: 14500,
        deposit: 20000,
        availableBeds: 1,
        totalBeds: 8,
        hasAttachedBath: true,
        hasAC: true,
        hasBalcony: true,
      },
      {
        id: 'r1-double',
        type: 'Double',
        rentPerMonth: 9500,
        deposit: 15000,
        availableBeds: 3,
        totalBeds: 16,
        hasAttachedBath: true,
        hasAC: true,
        hasBalcony: false,
      },
      {
        id: 'r1-triple',
        type: 'Triple',
        rentPerMonth: 8500,
        deposit: 12000,
        availableBeds: 2,
        totalBeds: 12,
        hasAttachedBath: true,
        hasAC: false,
        hasBalcony: false,
      },
    ],
    amenities: ['wifi', 'ac', 'food', 'laundry', 'attached_bath', 'power_backup', 'cctv', 'gym', 'ro_water', 'housekeeping'],
    rules: [
      'Gate closes at 11:30 PM (biometric re-entry with log)',
      'Visitors allowed in lounge until 8:00 PM',
      'No smoking or alcohol on premises',
      'Quiet hours observed between 11:00 PM and 7:00 AM',
    ],
    noticePeriodDays: 30,
    gateClosingTime: '11:30 PM',
    foodIncluded: true,
    verified: true,
    featured: true,
    contactPhone: '+91 98450 12345',
    contactEmail: 'stay@bluehavenpg.com',
    ownerName: 'Rajesh Sharma',
  },
  {
    id: 'prop-2',
    name: 'Serene Heights Premium PG for Women',
    tagline: 'Safe, gated biometric campus with round-the-clock female warden',
    gender: 'Girls',
    city: 'Bengaluru',
    locality: 'Koramangala, 5th Block',
    address: 'Near Sony World Signal, 5th Block Koramangala, Bengaluru, Karnataka 560095',
    lat: 12.9352,
    lng: 77.6245,
    coverImage: 'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=1000&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1540518614846-7ede433c4ef2?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1000&q=80',
    ],
    startingPrice: 9000,
    rating: 4.9,
    reviewCount: 92,
    rooms: [
      {
        id: 'r2-single',
        type: 'Single',
        rentPerMonth: 16000,
        deposit: 22000,
        availableBeds: 2,
        totalBeds: 6,
        hasAttachedBath: true,
        hasAC: true,
        hasBalcony: true,
      },
      {
        id: 'r2-double',
        type: 'Double',
        rentPerMonth: 11000,
        deposit: 16000,
        availableBeds: 4,
        totalBeds: 18,
        hasAttachedBath: true,
        hasAC: true,
        hasBalcony: true,
      },
      {
        id: 'r2-triple',
        type: 'Triple',
        rentPerMonth: 9000,
        deposit: 14000,
        availableBeds: 3,
        totalBeds: 12,
        hasAttachedBath: true,
        hasAC: false,
        hasBalcony: false,
      },
    ],
    amenities: ['wifi', 'ac', 'food', 'laundry', 'attached_bath', 'power_backup', 'cctv', 'gym', 'ro_water', 'housekeeping', 'geyser'],
    rules: [
      'Biometric access 24/7 with female security officer',
      'Parents permitted in visiting room',
      'Meal timings strictly observed: Breakfast 7:30-10:00, Dinner 8:00-10:30',
      'Notice period 30 days mandatory',
    ],
    noticePeriodDays: 30,
    gateClosingTime: '10:30 PM',
    foodIncluded: true,
    verified: true,
    featured: true,
    contactPhone: '+91 98860 99881',
    contactEmail: 'care@sereneheights.com',
    ownerName: 'Sunita Mehra',
  },
  {
    id: 'prop-3',
    name: 'CyberNest Unisex Smart Co-Living',
    tagline: 'Modern tech-worker hub near Hinjewadi Phase 1 with coworking lounge',
    gender: 'Unisex',
    city: 'Pune',
    locality: 'Hinjewadi Phase 1',
    address: 'Survey 48, Behind Rajiv Gandhi Infotech Park, Hinjewadi, Pune 411057',
    lat: 18.5913,
    lng: 73.7389,
    coverImage: 'https://images.unsplash.com/photo-1502005229762-ee1b2da97e06?auto=format&fit=crop&w=1000&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1502005229762-ee1b2da97e06?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1000&q=80',
    ],
    startingPrice: 7800,
    rating: 4.7,
    reviewCount: 48,
    rooms: [
      {
        id: 'r3-single',
        type: 'Single',
        rentPerMonth: 13500,
        deposit: 18000,
        availableBeds: 2,
        totalBeds: 10,
        hasAttachedBath: true,
        hasAC: true,
        hasBalcony: true,
      },
      {
        id: 'r3-double',
        type: 'Double',
        rentPerMonth: 9000,
        deposit: 13000,
        availableBeds: 5,
        totalBeds: 20,
        hasAttachedBath: true,
        hasAC: true,
        hasBalcony: false,
      },
      {
        id: 'r3-triple',
        type: 'Triple',
        rentPerMonth: 7800,
        deposit: 10000,
        availableBeds: 4,
        totalBeds: 15,
        hasAttachedBath: false,
        hasAC: false,
        hasBalcony: false,
      },
    ],
    amenities: ['wifi', 'ac', 'food', 'laundry', 'attached_bath', 'power_backup', 'cctv', 'gym', 'parking'],
    rules: [
      'Dedicated co-working desks open 24/7',
      'ID verification required for all non-resident guests',
      'Zero disturbance during remote working hours',
    ],
    noticePeriodDays: 20,
    gateClosingTime: '24 Hours Open',
    foodIncluded: true,
    verified: true,
    featured: false,
    contactPhone: '+91 97654 32109',
    contactEmail: 'info@cybernestpg.com',
    ownerName: 'Vikram Patel',
  },
  {
    id: 'prop-4',
    name: 'Gachibowli Silicon Stay PG',
    tagline: 'Premium fully-furnished AC suites near DLF Cyber City & Amazon HQ',
    gender: 'Boys',
    city: 'Hyderabad',
    locality: 'Gachibowli, Telecom Nagar',
    address: 'Road No 4, Telecom Nagar Extension, Gachibowli, Hyderabad 500032',
    lat: 17.4401,
    lng: 78.3489,
    coverImage: 'https://images.unsplash.com/photo-1598928506311-c55ded91a20c?auto=format&fit=crop&w=1000&q=80',
    galleryImages: [
      'https://images.unsplash.com/photo-1598928506311-c55ded91a20c?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1000&q=80',
    ],
    startingPrice: 8200,
    rating: 4.6,
    reviewCount: 39,
    rooms: [
      {
        id: 'r4-single',
        type: 'Single',
        rentPerMonth: 15000,
        deposit: 20000,
        availableBeds: 1,
        totalBeds: 5,
        hasAttachedBath: true,
        hasAC: true,
        hasBalcony: true,
      },
      {
        id: 'r4-double',
        type: 'Double',
        rentPerMonth: 9500,
        deposit: 14000,
        availableBeds: 3,
        totalBeds: 14,
        hasAttachedBath: true,
        hasAC: true,
        hasBalcony: false,
      },
      {
        id: 'r4-triple',
        type: 'Triple',
        rentPerMonth: 8200,
        deposit: 11000,
        availableBeds: 2,
        totalBeds: 9,
        hasAttachedBath: true,
        hasAC: false,
        hasBalcony: false,
      },
    ],
    amenities: ['wifi', 'ac', 'food', 'laundry', 'attached_bath', 'power_backup', 'cctv', 'parking', 'housekeeping'],
    rules: [
      'Vegetarian and Non-Vegetarian South/North Indian food separate prep',
      'Gate closes at midnight 12:00 AM',
      'Security deposit refundable on 30-day exit notice',
    ],
    noticePeriodDays: 30,
    gateClosingTime: '12:00 AM',
    foodIncluded: true,
    verified: true,
    featured: false,
    contactPhone: '+91 94401 55667',
    contactEmail: 'manager@siliconstay.in',
    ownerName: 'Kishore Reddy',
  },
];

export const INITIAL_RESIDENTS: Resident[] = [
  {
    id: 'res-1',
    name: 'Ananya Sen',
    email: 'ananya.sen@example.com',
    phone: '+91 98765 43210',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '204',
    roomType: 'Double',
    bedNumber: 'Bed B',
    monthlyRent: 9500,
    depositAmount: 15000,
    moveInDate: '2025-08-15',
    rentStatus: 'Pending',
    rentDueDate: '2026-09-07',
    lastPaymentDate: '2026-08-05',
    emergencyContact: '+91 98300 11223 (Father)',
    kycVerified: true,
    notes: 'Software Engineer at Infosys',
  },
  {
    id: 'res-2',
    name: 'Rohan Deshmukh',
    email: 'rohan.d@example.com',
    phone: '+91 98111 22334',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '101',
    roomType: 'Single',
    bedNumber: 'Bed A',
    monthlyRent: 14500,
    depositAmount: 20000,
    moveInDate: '2025-06-01',
    rentStatus: 'Paid',
    rentDueDate: '2026-09-05',
    lastPaymentDate: '2026-09-01',
    emergencyContact: '+91 98222 33445 (Brother)',
    kycVerified: true,
    notes: 'Product Designer at Swiggy',
  },
  {
    id: 'res-3',
    name: 'Tanmay Bhattacharya',
    email: 'tanmay.b@example.com',
    phone: '+91 97333 44556',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '204',
    roomType: 'Double',
    bedNumber: 'Bed A',
    monthlyRent: 9500,
    depositAmount: 15000,
    moveInDate: '2025-09-10',
    rentStatus: 'Paid',
    rentDueDate: '2026-09-05',
    lastPaymentDate: '2026-09-02',
    emergencyContact: '+91 98311 55667 (Mother)',
    kycVerified: true,
    notes: 'Data Analyst at Flipkart',
  },
  {
    id: 'res-4',
    name: 'Karthik Raja',
    email: 'karthik.r@example.com',
    phone: '+91 94444 88990',
    avatar: 'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&w=200&q=80',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '302',
    roomType: 'Triple',
    bedNumber: 'Bed C',
    monthlyRent: 8500,
    depositAmount: 12000,
    moveInDate: '2026-01-10',
    rentStatus: 'Overdue',
    rentDueDate: '2026-09-01',
    lastPaymentDate: '2026-07-31',
    emergencyContact: '+91 94433 22110 (Uncle)',
    kycVerified: false,
    notes: 'Awaiting college graduation verification',
  },
];

export const INITIAL_BOOKING_REQUESTS: BookingRequest[] = [
  {
    id: 'req-1',
    applicantName: 'Vikrant Chaudhary',
    email: 'vikrant.c@example.com',
    phone: '+91 98990 12345',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomType: 'Double',
    preferredMoveInDate: '2026-09-15',
    occupancyType: 'Working Professional',
    status: 'Pending',
    requestDate: '2026-09-03',
    message: 'Joining Wipro HSR campus. Need food included and quiet desk space.',
  },
  {
    id: 'req-2',
    applicantName: 'Deepak Nair',
    email: 'deepak.n@example.com',
    phone: '+91 98470 56789',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomType: 'Single',
    preferredMoveInDate: '2026-09-20',
    occupancyType: 'Working Professional',
    status: 'Pending',
    requestDate: '2026-09-04',
    message: 'Looking for single occupancy room with attached bathroom.',
  },
];

export const INITIAL_ATTENDANCE: AttendanceRecord[] = [
  {
    id: 'att-1',
    personId: 'res-1',
    personName: 'Ananya Sen',
    personType: 'Resident',
    roomNumber: '204',
    date: '2026-09-05',
    timestamp: '08:45 AM',
    type: 'Check-Out',
    status: 'On-Time',
    notes: 'Heading to office',
  },
  {
    id: 'att-2',
    personId: 'res-2',
    personName: 'Rohan Deshmukh',
    personType: 'Resident',
    roomNumber: '101',
    date: '2026-09-05',
    timestamp: '09:15 AM',
    type: 'Check-Out',
    status: 'On-Time',
  },
  {
    id: 'att-3',
    personId: 'staff-1',
    personName: 'Sunil Kumar',
    personType: 'Staff',
    role: 'Supervisor',
    date: '2026-09-05',
    timestamp: '06:30 AM',
    type: 'Check-In',
    status: 'On-Time',
    notes: 'Morning shift opening',
  },
  {
    id: 'att-4',
    personId: 'res-3',
    personName: 'Tanmay Bhattacharya',
    personType: 'Resident',
    roomNumber: '204',
    date: '2026-09-04',
    timestamp: '10:45 PM',
    type: 'Check-In',
    status: 'Late',
    notes: 'Late client meeting approval',
  },
];

export const INITIAL_STAFF: StaffMember[] = [
  {
    id: 'staff-1',
    name: 'Sunil Kumar',
    role: 'Manager',
    phone: '+91 98711 54321',
    avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=200&q=80',
    propertyId: 'prop-1',
    shift: 'Morning (6 AM - 2 PM)',
    todayStatus: 'Checked-In',
    lastClockIn: '06:30 AM',
  },
  {
    id: 'staff-2',
    name: 'Ramesh Yadav',
    role: 'Mess Cook',
    phone: '+91 97188 23456',
    avatar: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?auto=format&fit=crop&w=200&q=80',
    propertyId: 'prop-1',
    shift: 'Morning (6 AM - 2 PM)',
    todayStatus: 'Checked-In',
    lastClockIn: '05:45 AM',
  },
  {
    id: 'staff-3',
    name: 'Manoj Singh',
    role: 'Housekeeping',
    phone: '+91 98100 87654',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=200&q=80',
    propertyId: 'prop-1',
    shift: 'Morning (6 AM - 2 PM)',
    todayStatus: 'Checked-In',
    lastClockIn: '07:00 AM',
  },
  {
    id: 'staff-4',
    name: 'Bhawani Shankar',
    role: 'Security Guard',
    phone: '+91 99555 12121',
    avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=200&q=80',
    propertyId: 'prop-1',
    shift: 'Night (10 PM - 6 AM)',
    todayStatus: 'Checked-Out',
    lastClockIn: 'Yesterday 10:00 PM',
  },
];

export const INITIAL_TASKS: StaffTask[] = [
  {
    id: 'task-1',
    title: 'Daily Deep Cleaning 2nd Floor Corridor',
    description: 'Mop floor with disinfectant, clean shoe racks and dust window sills.',
    category: 'Cleaning',
    assignedToName: 'Manoj Singh',
    timeSlot: '09:00 AM - 10:30 AM',
    priority: 'High',
    completed: true,
  },
  {
    id: 'task-2',
    title: 'Inspect RO Purifier TDS & Water Level',
    description: 'Check TDS reading on 1st & 3rd floor water dispensers and log values.',
    category: 'Maintenance',
    assignedToName: 'Sunil Kumar',
    timeSlot: '11:00 AM - 11:30 AM',
    priority: 'Medium',
    completed: false,
  },
  {
    id: 'task-3',
    title: 'Lunch Preparation & Dining Hall Setup',
    description: 'Dal Tadka, Paneer Butter Masala, Jeera Rice, Phulkas, Fresh Salad.',
    category: 'Kitchen',
    assignedToName: 'Ramesh Yadav',
    timeSlot: '11:30 AM - 01:30 PM',
    priority: 'High',
    completed: false,
  },
  {
    id: 'task-4',
    title: 'Biometric Access Scanner Audit',
    description: 'Sync attendance cloud backup and clean scanner optical lens.',
    category: 'Security',
    assignedToName: 'Sunil Kumar',
    timeSlot: '04:00 PM - 05:00 PM',
    priority: 'Low',
    completed: false,
  },
];

export const INITIAL_BROADCASTS: BroadcastNotification[] = [
  {
    id: 'b-1',
    title: 'Monthly Rent Reminder (September 2026)',
    message: 'Dear residents, kindly clear your room rent by the 7th of September to avoid late clearance charges. Instant UPI receipts available in your portal.',
    category: 'Rent',
    target: 'All Residents',
    timestamp: '2026-09-02 10:00 AM',
    sender: 'Property Owner (Rajesh Sharma)',
  },
  {
    id: 'b-2',
    title: 'Overhead Water Tank Cleaning Notice',
    message: 'Water supply will be temporarily paused between 2:00 PM and 4:00 PM this coming Saturday for scheduled quarterly tank sanitization.',
    category: 'Maintenance',
    target: 'All Residents',
    timestamp: '2026-09-04 03:30 PM',
    sender: 'Supervisor Sunil',
  },
  {
    id: 'b-3',
    title: 'Sunday Special Dinner: Dum Biryani & Gulab Jamun',
    message: 'This Sunday dinner features Hyderabadi Veg/Paneer Dum Biryani, Chicken Dum Biryani, Mirchi ka Salan, and hot Gulab Jamun!',
    category: 'Food',
    target: 'All Residents',
    timestamp: '2026-09-04 07:00 PM',
    sender: 'Mess Chef Ramesh',
  },
];

export const INITIAL_MEAL_PLAN: MealPlanDay[] = [
  {
    day: 'Monday',
    breakfast: 'Masala Idli & Crispy Vada with Coconut Chutney, Sambar, Tea/Coffee',
    lunch: 'Steamed Rice, Yellow Dal Tadka, Aloo Gobhi, Phulkas, Curd & Salad',
    snacks: 'Adrak Chai with Onion Pakodas / Biscuits',
    dinner: 'Paneer Butter Masala, Roti, Mixed Veg Pulav, Boondi Raita',
    specialNote: 'Pure ghee tadka',
  },
  {
    day: 'Tuesday',
    breakfast: 'Aloo Paratha with Fresh Curd, Pickle, Mint Chutney, Milk/Tea',
    lunch: 'Rajma Masala, Steamed Basmati Rice, Bhindi Do Pyaza, Chapati, Papad',
    snacks: 'Filter Coffee with Poha / Chivda',
    dinner: 'Egg Curry / Mushroom Masala, Tawa Roti, Jeera Rice, Dal Fry',
  },
  {
    day: 'Wednesday',
    breakfast: 'Mysore Masala Dosa, Potato Palya, Tomato & Coconut Chutney',
    lunch: 'Kadhi Pakoda, Kichdi / Steamed Rice, Baingan Bharta, Salad',
    snacks: 'Masala Chai with Samosas',
    dinner: 'Chicken Curry / Shahi Paneer, Garlic Naan / Tawa Roti, Veg Biryani',
    specialNote: 'Non-veg & Veg options available',
  },
  {
    day: 'Thursday',
    breakfast: 'Poha with Roasted Peanuts, Sev, Lemon & Boiled Eggs / Sprouts',
    lunch: 'Chole Masala, Bhature / Chapati, Jeera Rice, Cucumber Raita',
    snacks: 'Green Tea / Regular Tea with Veg Cutlets',
    dinner: 'Mix Veg Korma, Chapati, Dal Makhani, Steamed Rice',
  },
  {
    day: 'Friday',
    breakfast: 'Uttapam with Sambar, Chana Dal Chutney, Fresh Fruits',
    lunch: 'Dal Palak, Aloo Shimla Mirch, Phulkas, Steamed Rice, Rasam',
    snacks: 'Hot Bournvita / Chai with Banana Cake',
    dinner: 'Matar Paneer, Tawa Roti, Veg Pulao, Gulab Jamun',
  },
  {
    day: 'Saturday',
    breakfast: 'Poori with Spiced Aloo Sabzi, Halwa, Tea/Coffee',
    lunch: 'South Indian Thali: Rice, Sambar, Rasam, Poriyal, Curd, Appalam',
    snacks: 'Chai with Murukku & Mixture',
    dinner: 'Chinese Night: Veg Fried Rice, Hakka Noodles, Manchurian Gravy',
  },
  {
    day: 'Sunday',
    breakfast: 'Chole Kulche / Bread Omelette, Seasonal Juice, Tea',
    lunch: 'Hyderabadi Dum Biryani (Chicken & Paneer), Mirchi Ka Salan, Raita',
    snacks: 'Sweet Lassi & Roasted Makhana',
    dinner: 'Light Khichdi, Tomato Soup, Papad, Roasted Paneer Tikka',
    specialNote: 'Grand Weekend Feast!',
  },
];

export const INITIAL_CHAT: ChatMessage[] = [
  {
    id: 'msg-1',
    senderRole: 'owner',
    senderName: 'Rajesh Sharma (Owner)',
    recipientName: 'Ananya Sen',
    text: 'Hello Ananya! Welcome to Blue Haven PG. Hope your room 204 is comfortable.',
    timestamp: 'Yesterday 10:15 AM',
    isOwner: true,
  },
  {
    id: 'msg-2',
    senderRole: 'resident',
    senderName: 'Ananya Sen',
    recipientName: 'Rajesh Sharma',
    text: 'Hi Rajesh ji, thanks a lot! Everything is great, just wanted to check if the 2nd floor WiFi router can be restarted as ping was fluctuating slightly.',
    timestamp: 'Yesterday 11:30 AM',
    isOwner: false,
  },
  {
    id: 'msg-3',
    senderRole: 'owner',
    senderName: 'Rajesh Sharma (Owner)',
    recipientName: 'Ananya Sen',
    text: 'Certainly! I have asked Sunil to reboot the dual-band mesh repeater. It should be 300 Mbps rock solid now.',
    timestamp: 'Yesterday 11:45 AM',
    isOwner: true,
  },
  {
    id: 'msg-4',
    senderRole: 'resident',
    senderName: 'Ananya Sen',
    recipientName: 'Rajesh Sharma',
    text: 'Working super smooth now! Also, I will be clearing this month rent today evening via UPI.',
    timestamp: 'Today 09:30 AM',
    isOwner: false,
  },
];

export const INITIAL_TICKETS: MaintenanceTicket[] = [
  {
    id: 'tkt-1',
    title: 'AC Remote Battery Depleted',
    category: 'Electrical',
    roomNumber: '204',
    residentName: 'Ananya Sen',
    description: 'The split AC remote is not turning on. Needs new AAA batteries or replacement.',
    priority: 'Normal',
    status: 'In-Progress',
    createdAt: '2026-09-04',
  },
  {
    id: 'tkt-2',
    title: 'Bathroom Tap Slow Drip',
    category: 'Plumbing',
    roomNumber: '101',
    residentName: 'Rohan Deshmukh',
    description: 'Washbasin faucet has a minor continuous drip.',
    priority: 'Normal',
    status: 'Resolved',
    createdAt: '2026-09-02',
  },
];

export const INITIAL_USERS: UserAccount[] = [];

export const DEFAULT_ROLE_PERMISSIONS = {
  admin: {
    property: { view: true, create: true, edit: true, delete: true, publish: true, approve: true },
    rooms: { view: true, create: true, edit: true, delete: true, allocateBed: true },
    tenants: { view: true, create: true, edit: true, move: true, vacate: true, viewDocs: true },
    rent: { view: true, generate: true, collect: true, verify: true, refund: true },
    electricity: { view: true, enterReadings: true, editReadings: true, generateBills: true, approveBills: true },
    agreements: { create: true, view: true, verify: true, generate: true, download: true },
    maintenance: { create: true, assign: true, update: true, resolve: true },
    reports: { view: true, export: true },
  },
  owner: {
    property: { view: true, create: true, edit: true, delete: false, publish: true, approve: false },
    rooms: { view: true, create: true, edit: true, delete: true, allocateBed: true },
    tenants: { view: true, create: true, edit: true, move: true, vacate: true, viewDocs: true },
    rent: { view: true, generate: true, collect: true, verify: true, refund: true },
    electricity: { view: true, enterReadings: true, editReadings: true, generateBills: true, approveBills: true },
    agreements: { create: true, view: true, verify: true, generate: true, download: true },
    maintenance: { create: true, assign: true, update: true, resolve: true },
    reports: { view: true, export: true },
  },
  manager: {
    property: { view: true, create: false, edit: true, delete: false, publish: false, approve: false },
    rooms: { view: true, create: true, edit: true, delete: false, allocateBed: true },
    tenants: { view: true, create: true, edit: true, move: true, vacate: true, viewDocs: true },
    rent: { view: true, generate: true, collect: true, verify: true, refund: false },
    electricity: { view: true, enterReadings: true, editReadings: true, generateBills: true, approveBills: false },
    agreements: { create: true, view: true, verify: true, generate: true, download: true },
    maintenance: { create: true, assign: true, update: true, resolve: true },
    reports: { view: true, export: true },
  },
  warden: {
    property: { view: true, create: false, edit: false, delete: false, publish: false, approve: false },
    rooms: { view: true, create: false, edit: false, delete: false, allocateBed: true },
    tenants: { view: true, create: false, edit: false, move: false, vacate: false, viewDocs: false },
    rent: { view: true, generate: false, collect: false, verify: false, refund: false },
    electricity: { view: true, enterReadings: true, editReadings: false, generateBills: false, approveBills: false },
    agreements: { create: false, view: true, verify: false, generate: false, download: false },
    maintenance: { create: true, assign: true, update: true, resolve: true },
    reports: { view: false, export: false },
  },
  accountant: {
    property: { view: true, create: false, edit: false, delete: false, publish: false, approve: false },
    rooms: { view: true, create: false, edit: false, delete: false, allocateBed: false },
    tenants: { view: true, create: false, edit: false, move: false, vacate: false, viewDocs: false },
    rent: { view: true, generate: true, collect: true, verify: true, refund: true },
    electricity: { view: true, enterReadings: false, editReadings: false, generateBills: true, approveBills: true },
    agreements: { create: false, view: true, verify: false, generate: false, download: true },
    maintenance: { view: true, assign: false, update: false, resolve: false, create: false },
    reports: { view: true, export: true },
  },
  staff: {
    property: { view: true, create: false, edit: false, delete: false, publish: false, approve: false },
    rooms: { view: true, create: false, edit: false, delete: false, allocateBed: false },
    tenants: { view: false, create: false, edit: false, move: false, vacate: false, viewDocs: false },
    rent: { view: false, generate: false, collect: false, verify: false, refund: false },
    electricity: { view: true, enterReadings: true, editReadings: false, generateBills: false, approveBills: false },
    agreements: { create: false, view: false, verify: false, generate: false, download: false },
    maintenance: { create: true, assign: false, update: true, resolve: true },
    reports: { view: false, export: false },
  },
  resident: {
    property: { view: true, create: false, edit: false, delete: false, publish: false, approve: false },
    rooms: { view: true, create: false, edit: false, delete: false, allocateBed: false },
    tenants: { view: false, create: false, edit: false, move: false, vacate: false, viewDocs: false },
    rent: { view: true, generate: false, collect: false, verify: false, refund: false },
    electricity: { view: true, enterReadings: false, editReadings: false, generateBills: false, approveBills: false },
    agreements: { create: false, view: true, verify: false, generate: false, download: true },
    maintenance: { create: true, assign: false, update: false, resolve: false },
    reports: { view: false, export: false },
  },
  public: {
    property: { view: true, create: false, edit: false, delete: false, publish: false, approve: false },
    rooms: { view: true, create: false, edit: false, delete: false, allocateBed: false },
    tenants: { view: false, create: false, edit: false, move: false, vacate: false, viewDocs: false },
    rent: { view: false, generate: false, collect: false, verify: false, refund: false },
    electricity: { view: false, enterReadings: false, editReadings: false, generateBills: false, approveBills: false },
    agreements: { create: false, view: false, verify: false, generate: false, download: false },
    maintenance: { create: false, assign: false, update: false, resolve: false },
    reports: { view: false, export: false },
  },
};

export const INITIAL_BEDS = [
  { id: 'bed-101-A', bedNumber: '101-A', roomId: 'r1-single', roomNumber: '101', propertyId: 'prop-1', floor: 1, building: 'Tower A', sharingType: 'Single' as const, status: 'Occupied' as const, currentTenantId: 'res-2', currentTenantName: 'Rohan Deshmukh', monthlyRent: 14500, deposit: 20000 },
  { id: 'bed-102-A', bedNumber: '102-A', roomId: 'r1-single', roomNumber: '102', propertyId: 'prop-1', floor: 1, building: 'Tower A', sharingType: 'Single' as const, status: 'Available' as const, monthlyRent: 14500, deposit: 20000 },
  { id: 'bed-201-A', bedNumber: '201-A', roomId: 'r1-double', roomNumber: '201', propertyId: 'prop-1', floor: 2, building: 'Tower A', sharingType: 'Double' as const, status: 'Occupied' as const, currentTenantId: 'res-3', currentTenantName: 'Priya Iyer', monthlyRent: 9500, deposit: 15000 },
  { id: 'bed-201-B', bedNumber: '201-B', roomId: 'r1-double', roomNumber: '201', propertyId: 'prop-1', floor: 2, building: 'Tower A', sharingType: 'Double' as const, status: 'Booking Pending' as const, monthlyRent: 9500, deposit: 15000 },
  { id: 'bed-204-A', bedNumber: '204-A', roomId: 'r1-single', roomNumber: '204', propertyId: 'prop-1', floor: 2, building: 'Tower A', sharingType: 'Single' as const, status: 'Occupied' as const, currentTenantId: 'res-1', currentTenantName: 'Ananya Sen', monthlyRent: 13500, deposit: 20000 },
  { id: 'bed-301-A', bedNumber: '301-A', roomId: 'r1-triple', roomNumber: '301', propertyId: 'prop-1', floor: 3, building: 'Tower A', sharingType: 'Triple' as const, status: 'Occupied' as const, currentTenantId: 'res-4', currentTenantName: 'Karthik Raja', monthlyRent: 8500, deposit: 12000 },
  { id: 'bed-301-B', bedNumber: '301-B', roomId: 'r1-triple', roomNumber: '301', propertyId: 'prop-1', floor: 3, building: 'Tower A', sharingType: 'Triple' as const, status: 'Cleaning' as const, monthlyRent: 8500, deposit: 12000, nextAvailableDate: '2026-09-08' },
  { id: 'bed-301-C', bedNumber: '301-C', roomId: 'r1-triple', roomNumber: '301', propertyId: 'prop-1', floor: 3, building: 'Tower A', sharingType: 'Triple' as const, status: 'Available' as const, monthlyRent: 8500, deposit: 12000 },
  { id: 'bed-302-A', bedNumber: '302-A', roomId: 'r1-double', roomNumber: '302', propertyId: 'prop-1', floor: 3, building: 'Tower A', sharingType: 'Double' as const, status: 'Notice Period' as const, currentTenantId: 'res-5', currentTenantName: 'Aditya Varma', monthlyRent: 9500, deposit: 15000, nextAvailableDate: '2026-09-30' },
  { id: 'bed-302-B', bedNumber: '302-B', roomId: 'r1-double', roomNumber: '302', propertyId: 'prop-1', floor: 3, building: 'Tower A', sharingType: 'Double' as const, status: 'Ready' as const, monthlyRent: 9500, deposit: 15000 },
];

export const INITIAL_LEADS = [
  {
    id: 'lead-1',
    name: 'Siddharth Roy',
    phone: '+91 99887 76655',
    email: 'siddharth.roy@gmail.com',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomTypePreference: 'Single' as const,
    budgetMax: 15000,
    preferredMoveIn: '2026-09-15',
    stage: 'Visit Scheduled' as const,
    source: 'Google Search',
    assignedTo: 'Kavita Menon',
    notes: 'Works at Oracle HSR. Wants quiet single room with high-speed WiFi for work-from-home.',
    createdAt: '2026-09-01',
    lastFollowUp: '2026-09-04',
  },
  {
    id: 'lead-2',
    name: 'Megha Nair',
    phone: '+91 98451 22334',
    email: 'megha.nair@outlook.com',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomTypePreference: 'Double' as const,
    budgetMax: 10000,
    preferredMoveIn: '2026-09-20',
    stage: 'Interested' as const,
    source: 'Walk-In / Referral',
    assignedTo: 'Devendra Patil',
    notes: 'Visited yesterday, liked food quality and gym. Awaiting parents confirmation.',
    createdAt: '2026-09-02',
    lastFollowUp: '2026-09-04',
  },
  {
    id: 'lead-3',
    name: 'Abhishek Kulkarni',
    phone: '+91 97112 33445',
    email: 'abhi.kulkarni@gmail.com',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomTypePreference: 'Triple' as const,
    budgetMax: 9000,
    preferredMoveIn: '2026-10-01',
    stage: 'Contacted' as const,
    source: 'Website Form',
    assignedTo: 'Kavita Menon',
    notes: 'College student at NIFT Bengaluru. Requested virtual tour link.',
    createdAt: '2026-09-03',
    lastFollowUp: '2026-09-04',
  },
];

export const INITIAL_METER_READINGS = [
  {
    id: 'emr-1',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '204',
    meterNumber: 'MTR-HSR-204',
    previousReading: 1240,
    currentReading: 1390,
    unitsConsumed: 150,
    ratePerUnit: 8.5,
    fixedCharges: 75,
    totalAmount: 1350,
    readingDate: '2026-09-01',
    staffName: 'Sunil Kumar',
    meterPhotoUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=400&q=80',
    status: 'Verified' as const,
    isAnomaly: false,
  },
  {
    id: 'emr-2',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '101',
    meterNumber: 'MTR-HSR-101',
    previousReading: 2100,
    currentReading: 2280,
    unitsConsumed: 180,
    ratePerUnit: 8.5,
    fixedCharges: 75,
    totalAmount: 1605,
    readingDate: '2026-09-01',
    staffName: 'Devendra Patil',
    meterPhotoUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=400&q=80',
    status: 'Verified' as const,
    isAnomaly: false,
  },
  {
    id: 'emr-3',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '301',
    meterNumber: 'MTR-HSR-301',
    previousReading: 950,
    currentReading: 1380,
    unitsConsumed: 430,
    ratePerUnit: 8.5,
    fixedCharges: 75,
    totalAmount: 3730,
    readingDate: '2026-09-01',
    staffName: 'Sunil Kumar',
    meterPhotoUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=400&q=80',
    status: 'Flagged' as const,
    isAnomaly: true,
    anomalyReason: 'Sudden 180% surge compared to 3-month average of 150 units. Suspected continuous AC usage or wiring leak.',
  },
];

export const INITIAL_RECONCILIATION = [
  {
    id: 'rec-aug-2026',
    month: 'August 2026',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    actualProviderBill: 24800,
    totalUnitsProvider: 2950,
    tenantChargesGenerated: 27200,
    tenantCollected: 25400,
    tenantOutstanding: 1800,
    variance: 2400,
  },
];

export const INITIAL_DEPOSITS = [
  {
    id: 'dep-1',
    residentId: 'res-1',
    residentName: 'Ananya Sen',
    roomNumber: '204',
    propertyId: 'prop-1',
    depositReceived: 20000,
    receivedDate: '2026-02-01',
    status: 'Held' as const,
    deductions: [],
    totalDeductions: 0,
    finalRefundAmount: 20000,
    notes: 'Security deposit held in escrow account. Eligible for full refund upon 30-day notice.',
  },
  {
    id: 'dep-2',
    residentId: 'res-5',
    residentName: 'Aditya Varma',
    roomNumber: '302',
    propertyId: 'prop-1',
    depositReceived: 15000,
    receivedDate: '2025-10-01',
    status: 'Settlement Pending' as const,
    deductions: [
      {
        id: 'ded-1',
        category: 'Damage' as const,
        amount: 1200,
        reason: 'Broken study desk drawer sliding track',
        approvedByOwner: true,
      },
      {
        id: 'ded-2',
        category: 'Electricity' as const,
        amount: 850,
        reason: 'Final pro-rated electricity consumption for September',
        approvedByOwner: true,
      },
    ],
    totalDeductions: 2050,
    finalRefundAmount: 12950,
    notes: 'Tenant on notice period ending Sept 30. Move-out inspection signed.',
  },
];

export const INITIAL_AGREEMENTS = [
  {
    id: 'agr-1',
    agreementNumber: 'PGN-BLR-2026-0089',
    residentId: 'res-1',
    residentName: 'Ananya Sen',
    parentGuardianName: 'Debabrata Sen',
    tenantDOB: '2001-05-14',
    tenantPermanentAddress: 'Flat 4B, Greenwood Apts, Salt Lake City, Kolkata, WB 700064',
    tenantCurrentAddress: 'Room 204, Blue Haven PG, 14th Main Rd, HSR Sector 3, Bengaluru',
    tenantCollegeOrOffice: 'Infosys Limited, Electronic City Phase 1, Bengaluru',
    tenantIdDocumentType: 'Aadhaar' as const,
    tenantIdDocumentMasked: 'XXXX-XXXX-8492',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    propertyAddress: 'Plot 412, 14th Main Rd, Sector 3, HSR Layout, Bengaluru, Karnataka 560102',
    ownerName: 'Rajesh Sharma',
    ownerPhone: '+91 98450 12345',
    roomNumber: '204',
    bedNumber: '204-A',
    monthlyRent: 13500,
    securityDeposit: 20000,
    electricityTerms: 'Billed monthly per sub-meter @ ₹8.50/unit consumed.',
    noticePeriodDays: 30,
    startDate: '2026-02-01',
    endDate: '2027-01-31',
    rulesSummary: [
      'Gate curfew strictly at 11:00 PM unless prior digital approval obtained',
      'No smoking or unauthorized alcohol consumption inside rooms or common areas',
      'Overnight guests require warden approval 24 hours in advance',
      'Quiet hours observed between 10:30 PM and 7:00 AM',
    ],
    emergencyContactName: 'Debabrata Sen (Father)',
    emergencyContactPhone: '+91 94330 11223',
    status: 'Active' as const,
    ownerSigned: true,
    tenantSigned: true,
    signedDate: '2026-02-02',
  },
];

export const INITIAL_VISITORS = [
  {
    id: 'vis-1',
    residentId: 'res-1',
    residentName: 'Ananya Sen',
    roomNumber: '204',
    visitorName: 'Ritu Sen (Sister)',
    visitorPhone: '+91 98300 45678',
    purpose: 'Family visit & lunch',
    expectedArrival: '2026-09-05 13:00',
    checkInTime: '2026-09-05 13:15',
    checkOutTime: '2026-09-05 16:30',
    status: 'Checked-Out' as const,
    approvedBy: 'Devendra Patil (Warden)',
  },
  {
    id: 'vis-2',
    residentId: 'res-2',
    residentName: 'Rohan Deshmukh',
    roomNumber: '101',
    visitorName: 'Aman Verma',
    visitorPhone: '+91 99112 33445',
    purpose: 'College project discussion',
    expectedArrival: '2026-09-05 18:00',
    status: 'Pre-Approved' as const,
    approvedBy: 'Auto pre-approved by resident',
  },
];

export const INITIAL_AUDIT_LOGS = [
  {
    id: 'aud-1',
    userId: 'user-owner',
    userName: 'Rajesh Sharma',
    userRole: 'owner',
    action: 'Rent Status Updated',
    entity: 'Resident: Ananya Sen',
    timestamp: '2026-09-04 10:24',
    details: 'Marked August rent ₹13,500 as Paid via UPI transaction.',
  },
  {
    id: 'aud-2',
    userId: 'user-warden',
    userName: 'Devendra Patil',
    userRole: 'warden',
    action: 'Electricity Meter Logged',
    entity: 'Room 204 Meter',
    timestamp: '2026-09-01 09:15',
    details: 'Logged reading 1390 units with verified photo proof.',
  },
  {
    id: 'aud-3',
    userId: 'user-owner',
    userName: 'Rajesh Sharma',
    userRole: 'owner',
    action: 'New Staff Assigned',
    entity: 'Bhawani Shankar (Security Guard)',
    timestamp: '2026-08-28 14:00',
    details: 'Assigned to Night Shift (10 PM - 6 AM) at Tower A.',
  },
];

export const INITIAL_SETTINGS = {
  currency: 'INR (₹)',
  rentDueDay: 5,
  lateFeeGraceDays: 5,
  lateFeeAmount: 250,
  defaultElectricityRate: 8.5,
  electricityBillingModel: 'Room-Level' as const,
  depositRefundNoticeDays: 30,
  emergencyMaintenanceSlaHours: 2,
  highMaintenanceSlaHours: 6,
  normalMaintenanceSlaHours: 24,
  lowMaintenanceSlaHours: 48,
};

// ==========================================
// ENTERPRISE SEED DATA FOR STAYS, INVOICES & SETTLEMENTS
// ==========================================

export const INITIAL_STAYS = [
  {
    id: 'stay-1',
    residentId: 'res-1',
    residentName: 'Ananya Sen',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '204',
    bedNumber: 'Bed B',
    roomType: 'Double' as const,
    startDate: '2025-08-15',
    monthlyRent: 9500,
    status: 'Active' as const,
    createdAt: '2025-08-15',
  },
  {
    id: 'stay-2',
    residentId: 'res-2',
    residentName: 'Rohan Deshmukh',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '101',
    bedNumber: 'Bed A',
    roomType: 'Single' as const,
    startDate: '2025-06-01',
    monthlyRent: 14500,
    status: 'Active' as const,
    createdAt: '2025-06-01',
  },
  {
    id: 'stay-3',
    residentId: 'res-3',
    residentName: 'Tanmay Bhattacharya',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '204',
    bedNumber: 'Bed A',
    roomType: 'Double' as const,
    startDate: '2025-09-10',
    monthlyRent: 9500,
    status: 'Active' as const,
    createdAt: '2025-09-10',
  },
  {
    id: 'stay-4',
    residentId: 'res-4',
    residentName: 'Karthik Raja',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '302',
    bedNumber: 'Bed C',
    roomType: 'Triple' as const,
    startDate: '2026-01-10',
    monthlyRent: 8500,
    status: 'Active' as const,
    createdAt: '2026-01-10',
  },
];

export const INITIAL_INVOICES = [
  {
    id: 'inv-101',
    invoiceNumber: 'INV-2026-09-001',
    residentId: 'res-1',
    residentName: 'Ananya Sen',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '204',
    month: 'September 2026',
    billingCycleStart: '2026-09-01',
    billingCycleEnd: '2026-09-30',
    dueDate: '2026-09-07',
    baseRent: 9500,
    electricityCharges: 480,
    otherCharges: 0,
    lineItems: [
      { id: 'li-1', description: 'September Room Rent (Double Sharing)', amount: 9500, type: 'Rent' as const },
      { id: 'li-2', description: 'Room Sub-meter Power (48 units @ ₹10/unit)', amount: 480, type: 'Electricity' as const },
    ],
    subtotal: 9980,
    previousDuesApplied: 0,
    advanceDeducted: 0,
    totalDue: 9980,
    amountPaid: 0,
    outstandingBalance: 9980,
    status: 'Due' as const,
    createdAt: '2026-09-01',
    updatedAt: '2026-09-01',
    receiptIds: [],
  },
  {
    id: 'inv-102',
    invoiceNumber: 'INV-2026-09-002',
    residentId: 'res-2',
    residentName: 'Rohan Deshmukh',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '101',
    month: 'September 2026',
    billingCycleStart: '2026-09-01',
    billingCycleEnd: '2026-09-30',
    dueDate: '2026-09-05',
    baseRent: 14500,
    electricityCharges: 620,
    otherCharges: 0,
    lineItems: [
      { id: 'li-3', description: 'September Room Rent (Single AC Suite)', amount: 14500, type: 'Rent' as const },
      { id: 'li-4', description: 'Room AC Sub-meter Power (62 units)', amount: 620, type: 'Electricity' as const },
    ],
    subtotal: 15120,
    previousDuesApplied: 0,
    advanceDeducted: 0,
    totalDue: 15120,
    amountPaid: 15120,
    outstandingBalance: 0,
    status: 'Paid' as const,
    createdAt: '2026-09-01',
    updatedAt: '2026-09-02',
    receiptIds: ['rec-202'],
  },
  {
    id: 'inv-103',
    invoiceNumber: 'INV-2026-09-003',
    residentId: 'res-3',
    residentName: 'Tanmay Bhattacharya',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '204',
    month: 'September 2026',
    billingCycleStart: '2026-09-01',
    billingCycleEnd: '2026-09-30',
    dueDate: '2026-09-05',
    baseRent: 9500,
    electricityCharges: 480,
    otherCharges: 0,
    lineItems: [
      { id: 'li-5', description: 'September Room Rent (Double Sharing)', amount: 9500, type: 'Rent' as const },
      { id: 'li-6', description: 'Room Sub-meter Power', amount: 480, type: 'Electricity' as const },
    ],
    subtotal: 9980,
    previousDuesApplied: 0,
    advanceDeducted: 0,
    totalDue: 9980,
    amountPaid: 9980,
    outstandingBalance: 0,
    status: 'Paid' as const,
    createdAt: '2026-09-01',
    updatedAt: '2026-09-02',
    receiptIds: ['rec-203'],
  },
  {
    id: 'inv-104',
    invoiceNumber: 'INV-2026-09-004',
    residentId: 'res-4',
    residentName: 'Karthik Raja',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '302',
    month: 'September 2026',
    billingCycleStart: '2026-09-01',
    billingCycleEnd: '2026-09-30',
    dueDate: '2026-09-01',
    baseRent: 8500,
    electricityCharges: 350,
    otherCharges: 250,
    lineItems: [
      { id: 'li-7', description: 'September Room Rent (Triple Sharing)', amount: 8500, type: 'Rent' as const },
      { id: 'li-8', description: 'Electricity Sub-meter', amount: 350, type: 'Electricity' as const },
      { id: 'li-9', description: 'Late Payment Fee', amount: 250, type: 'LateFee' as const },
    ],
    subtotal: 9100,
    previousDuesApplied: 2000,
    advanceDeducted: 0,
    totalDue: 11100,
    amountPaid: 0,
    outstandingBalance: 11100,
    status: 'Overdue' as const,
    createdAt: '2026-09-01',
    updatedAt: '2026-09-02',
    receiptIds: [],
  },
];

export const INITIAL_CHECKOUT_SETTLEMENTS = [
  {
    id: 'chk-01',
    residentId: 'res-old-1',
    residentName: 'Saurav Joshi',
    propertyId: 'prop-1',
    propertyName: 'Blue Haven Luxury Living PG',
    roomNumber: '102',
    bedNumber: 'Bed B',
    checkoutDate: '2026-08-31',
    depositHeld: 15000,
    pendingRent: 0,
    electricityCharges: 450,
    foodCharges: 0,
    damageDeductions: 800,
    otherDeductions: 0,
    deductionNotes: 'Broken curtain rod and wall patch repair',
    totalDeductions: 1250,
    finalRefundAmount: 13750,
    refundStatus: 'Refunded' as const,
    refundTransactionId: 'UPI-REF-998822',
    settledAt: '2026-09-01',
    propertyChecklist: {
      keysReturned: true,
      bedInspected: true,
      furnitureChecked: true,
      accessCardReturned: true,
      roomCleaned: true,
    },
    notes: 'Smooth checkout; security deposit settled via UPI.',
  },
];


